import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/lib/auth-oidc'
import {
  usePolicies,
  usePolicyActions,
  useSetAccountPolicies,
  useSetSubscription,
  useSubscriptions,
} from '@/lib/api'

export const Route = createFileRoute('/settings/policies')({
  component: PoliciesPage,
})

function PoliciesPage() {
  const { t } = useTranslation()
  const { user, isLoading: authLoading } = useAuth()
  const { data: policiesData, isLoading: policiesLoading } = usePolicies()
  const { data: validActions, isLoading: actionsLoading } = usePolicyActions()
  const setMutation = useSetAccountPolicies()
  const { data: subscriptionsData, isLoading: subsLoading } = useSubscriptions()
  const setSubMutation = useSetSubscription()

  const accountNames = user?.accessible_accounts ?? []
  const isLoading =
    authLoading || policiesLoading || actionsLoading || subsLoading

  // Build a map: accountName -> set of denied actions
  const deniedMap = new Map<string, Set<string>>()
  if (policiesData?.policies) {
    for (const policy of policiesData.policies) {
      if (!policy.denied) continue
      let actions = deniedMap.get(policy.account_name)
      if (!actions) {
        actions = new Set()
        deniedMap.set(policy.account_name, actions)
      }
      actions.add(policy.action)
    }
  }

  // Build subscription map: accountName -> has_subscription
  const subscriptionMap = new Map<string, boolean>()
  if (subscriptionsData?.subscriptions) {
    for (const sub of subscriptionsData.subscriptions) {
      subscriptionMap.set(sub.account_name, sub.has_subscription)
    }
  }

  const handleToggle = (
    accountName: string,
    action: string,
    currentlyDenied: boolean,
  ) => {
    const existing = deniedMap.get(accountName)
    const currentDenied = existing ? new Set(existing) : new Set<string>()

    if (currentlyDenied) {
      currentDenied.delete(action)
    } else {
      currentDenied.add(action)
    }

    setMutation.mutate(
      {
        accountName,
        deniedActions: Array.from(currentDenied),
      },
      {
        onSuccess: () => {
          toast.success(t('policies.saved'))
        },
        onError: () => {
          toast.error(t('policies.error'))
        },
      },
    )
  }

  const handleSubscriptionToggle = (
    accountName: string,
    currentValue: boolean,
  ) => {
    setSubMutation.mutate(
      {
        accountName,
        hasSubscription: !currentValue,
      },
      {
        onSuccess: () => {
          toast.success(t('policies.saved'))
        },
        onError: () => {
          toast.error(t('policies.error'))
        },
      },
    )
  }

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/settings">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">{t('policies.title')}</h1>
              <p className="text-sm text-muted-foreground">
                {t('policies.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {isLoading ? (
            <LoadingSpinner label={t('policies.loading')} />
          ) : accountNames.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {t('policies.noAccounts')}
              </CardContent>
            </Card>
          ) : (
            accountNames.map((account) => {
              const accountDenied = deniedMap.get(account) ?? new Set<string>()

              return (
                <Card key={account}>
                  <CardHeader>
                    <CardTitle className="text-base">{account}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Subscription toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          {t('policies.subscription')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {subscriptionMap.get(account)
                            ? t('policies.subscriptionActive')
                            : t('policies.subscriptionInactive')}
                        </p>
                      </div>
                      <Switch
                        checked={subscriptionMap.get(account) ?? false}
                        onCheckedChange={() =>
                          handleSubscriptionToggle(
                            account,
                            subscriptionMap.get(account) ?? false,
                          )
                        }
                        disabled={setSubMutation.isPending}
                      />
                    </div>

                    <div className="border-t pt-3" />

                    {/* Policy action toggles */}
                    {(validActions ?? []).map((action) => {
                      const isDenied = accountDenied.has(action)

                      return (
                        <div
                          key={action}
                          className="flex items-center justify-between"
                        >
                          <div>
                            <p className="text-sm font-medium">
                              {t(`policies.actions.${action}`)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {isDenied
                                ? t('policies.denied')
                                : t('policies.allowed')}
                            </p>
                          </div>
                          <Switch
                            checked={isDenied}
                            onCheckedChange={() =>
                              handleToggle(account, action, isDenied)
                            }
                            disabled={setMutation.isPending}
                          />
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
